import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell } from "@/components/PageShell";

const Section = ({ title, children, testId }) => (
  <section
    className="glass glow-border rounded-2xl p-7 md:p-9"
    data-testid={testId}
  >
    <h2 className="font-display text-xl md:text-2xl text-amber-glow mb-5 tracking-tight">
      {title}
    </h2>
    <div className="space-y-3 text-sm md:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </section>
);

const Article = ({ num, title, children, testId }) => (
  <div className="border-l-2 border-amber-glow/40 pl-5" data-testid={testId}>
    <h3 className="font-display text-base md:text-lg text-slate-100 mb-2 tracking-tight">
      <span className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#00F0FF] mr-2">
        Article {num}
      </span>
      {title}
    </h3>
    <p className="text-sm text-slate-300 leading-relaxed">{children}</p>
  </div>
);

export default function MentionsLegales() {
  const { locale } = useLang();
  const isFr = locale === "fr";

  return (
    <PageShell
      testId="page-mentions-legales"
      title={isFr ? "Mentions Légales et CGV" : "Legal Notice & T&Cs"}
      subtitle={isFr
        ? "Transparence totale sur la structure officielle de FACOW360 — conformité, sécurité du client et conditions de vente."
        : "Total transparency on the official FACOW360 structure — compliance, client safety and sales conditions."}
    >
      {/* ───────── MENTIONS LÉGALES ───────── */}
      <Section
        title={isFr ? "MENTIONS LÉGALES" : "LEGAL NOTICE"}
        testId="legal-identity-block"
      >
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-1">
            {isFr ? "Éditeur du site" : "Site editor"}
          </span>
          {isFr
            ? "Fabrice Marini, Entrepreneur Individuel."
            : "Fabrice Marini, Individual Entrepreneur."}
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-1">
            {isFr ? "Siège social" : "Registered office"}
          </span>
          {isFr ? "Verviers, Belgique." : "Verviers, Belgium."}
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-1">
            {isFr ? "Numéro d'immatriculation" : "Registration number"}
          </span>
          {isFr
            ? "Numéro SIREN : 103413993 (SIRET : 10341399300017)."
            : "SIREN: 103413993 (SIRET: 10341399300017)."}
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-1">
            Contact
          </span>
          <a
            href="mailto:contact@facow360.com"
            className="text-[#00F0FF] hover:underline"
            data-testid="legal-contact-email"
          >
            contact@facow360.com
          </a>
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-1">
            {isFr ? "Hébergeur" : "Hosting provider"}
          </span>
          Émergent Labs Inc.
        </p>
      </Section>

      {/* ───────── CGV ───────── */}
      <Section
        title={isFr
          ? "CONDITIONS GÉNÉRALES DE VENTE (CGV)"
          : "GENERAL TERMS & CONDITIONS OF SALE (T&Cs)"}
        testId="legal-cgv-block"
      >
        <div className="space-y-6 pt-2">
          <Article
            num="1"
            title={isFr ? "Objet des prestations" : "Scope of services"}
            testId="cgv-article-1"
          >
            {isFr
              ? "Les prestations \"Facow360\" regroupent l'expertise technique (mesures d'ondes), le conseil en hygiène de l'habitat (Maison Saine), l'harmonisation énergétique et le conseil en Cuisine Vivante. L'objectif est l'optimisation de l'environnement et du capital énergétique global du client."
              : "\"Facow360\" services bring together technical expertise (wave measurements), home hygiene advice (Healthy Home), energetic harmonisation and Living Cuisine guidance. The aim is to optimise the client's environment and overall energetic capital."}
          </Article>

          <Article
            num="2"
            title={isFr
              ? "Cadre juridique, santé et nutrition"
              : "Legal framework, health and nutrition"}
            testId="cgv-article-2"
          >
            {isFr
              ? "Les interventions et conseils de Facow360 ne constituent en aucun cas un acte médical, un bilan diététique ou un diagnostic de santé. Fabrice Marini n'est pas médecin. Ses conseils visent exclusivement le bien-être environnemental et l'hygiène de vie, et ne doivent jamais entraîner l'interruption d'un traitement médical. Le client est seul responsable de la déclaration de ses allergies ou intolérances."
              : "Facow360's interventions and advice in no way constitute a medical act, a dietary assessment or a health diagnosis. Fabrice Marini is not a physician. His advice is exclusively aimed at environmental well-being and lifestyle hygiene, and must never lead to the interruption of medical care. The client alone is responsible for declaring any allergies or intolerances."}
          </Article>

          <Article
            num="3"
            title={isFr
              ? "Protocole et obligation de moyens"
              : "Protocol and obligation of means"}
            testId="cgv-article-3"
          >
            {isFr
              ? "Facow360 déploie un protocole utilisant des instruments de mesure de précision. L'efficacité des correctifs dépendant de la réceptivité propre à chaque organisme, Facow360 est tenu à une obligation de moyens et non de résultat absolu sur le ressenti physique."
              : "Facow360 deploys a protocol using precision measurement instruments. As the effectiveness of corrections depends on each organism's receptivity, Facow360 is bound by an obligation of means, not of absolute result on the physical sensation."}
          </Article>

          <Article
            num="4"
            title={isFr ? "Droit de rétractation" : "Right of withdrawal"}
            testId="cgv-article-4"
          >
            {isFr
              ? "Conformément à la législation, le client dispose d'un droit de rétractation de 14 jours. Toutefois, si la prestation commence à la demande du client, ce dernier renonce expressément à ce droit pour permettre l'exécution immédiate."
              : "In accordance with the legislation, the client has a 14-day right of withdrawal. However, if the service starts at the client's request, the latter expressly waives this right to allow immediate execution."}
          </Article>

          <Article
            num="5"
            title={isFr
              ? "Évolution de l'environnement"
              : "Environmental evolution"}
            testId="cgv-article-5"
          >
            {isFr
              ? "L'expertise reflète l'état du lieu au moment précis de l'intervention. Facow360 ne peut être tenu responsable des modifications ultérieures de l'électrosmog (nouvelles antennes, 5G, réseaux tiers) survenant après le passage de l'expert."
              : "The expertise reflects the state of the site at the precise moment of the intervention. Facow360 cannot be held responsible for subsequent changes to the electrosmog (new antennas, 5G, third-party networks) occurring after the expert's visit."}
          </Article>

          <Article
            num="6"
            title={isFr
              ? "Déplacements et règlement"
              : "Travel and payment"}
            testId="cgv-article-6"
          >
            {isFr
              ? "Les frais de déplacement sont inclus dans un rayon de 20 km autour du siège social ou du point de départ de l'expert. Au-delà, un forfait de 0,77 €/km est appliqué. Le règlement du solde s'effectue au comptant dès la fin de l'intervention ou de manière sécurisée en ligne pour les services à distance."
              : "Travel costs are included within a 20 km radius around the registered office or the expert's starting point. Beyond that, a flat rate of €0.77/km applies. Settlement of the balance is made in cash at the end of the intervention or securely online for remote services."}
          </Article>

          <Article
            num="7"
            title={isFr ? "Médiation et litiges" : "Mediation and disputes"}
            testId="cgv-article-7"
          >
            {isFr
              ? "En cas de litige, le client peut recourir gratuitement au médiateur de la consommation. À défaut, les tribunaux du siège social du prestataire sont seuls compétents."
              : "In case of dispute, the client may refer free of charge to the consumer mediator. Failing that, the courts of the provider's registered office have sole jurisdiction."}
          </Article>

          <Article
            num="8"
            title={isFr ? "Confidentialité" : "Confidentiality"}
            testId="cgv-article-8"
          >
            {isFr
              ? "Facow360 garantit le secret professionnel total sur les informations privées et les spécificités techniques relevées lors de l'audit."
              : "Facow360 guarantees total professional secrecy on the private information and technical specifics noted during the audit."}
          </Article>
        </div>
      </Section>
    </PageShell>
  );
}
